import { PDFDocument, PageSizes } from 'pdf-lib';
import { jsPDF } from 'jspdf';
import heic2any from 'heic2any';

export type ProcessedFile = {
  file: File;
  url: string;
  pageCount: number;
};

/**
 * Converts HEIC image to JPEG format
 */
export async function convertHeicToJpeg(file: File): Promise<File> {
  try {
    // Use heic2any to convert the HEIC file to a JPEG blob
    const jpegBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.8
    }) as Blob;
    
    // Create a new file name by replacing the extension
    const fileName = file.name.replace(/\.[^/.]+$/, '.jpg');
    
    // Return the new File object
    return new File([jpegBlob], fileName, { type: 'image/jpeg' });
  } catch (error) {
    console.error('Error converting HEIC to JPEG:', error);
    throw new Error('Failed to convert HEIC image');
  }
}

/**
 * Detect if a file is a HEIC image
 */
export function isHeicImage(file: File): boolean {
  return file.type === 'image/heic' || 
         file.type === 'image/heif' || 
         file.name.toLowerCase().endsWith('.heic') || 
         file.name.toLowerCase().endsWith('.heif');
}

/**
 * Detect if a file is a LibreOffice / Microsoft Office document
 */
export function isOfficeDocument(file: File): boolean {
  const officeExtensions = [
    '.docx', '.doc', '.pptx', '.ppt', '.xlsx', '.xls'
  ];
  
  return officeExtensions.some(ext => 
    file.name.toLowerCase().endsWith(ext)
  );
}

/**
 * Convert LibreOffice/Office documents to PDF using Gotenberg API
 */
export async function convertOfficeToPdf(file: File): Promise<File> {
  try {
    // Create form data for the Gotenberg API request
    const formData = new FormData();
    formData.append('files', file);
    
    // Make POST request to Gotenberg API with CORS handling
    const response = await fetch('https://convert.ctrlp.co.in/forms/libreoffice/convert', {
      method: 'POST',
      body: formData,
      mode: 'cors', // Try standard CORS mode first
      headers: {
        // Adding these headers might help if the server is configured to check them
        'Origin': window.location.origin,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Gotenberg API error: ${response.status} ${response.statusText}`);
    }
    
    // Get the PDF blob from the response
    const pdfBlob = await response.blob();
    
    // Create a new file name by replacing the extension
    const fileName = file.name.replace(/\.[^/.]+$/, '.pdf');
    
    // Return the new File object
    return new File([pdfBlob], fileName, { type: 'application/pdf' });
  } catch (error) {
    console.error('Error converting Office document to PDF:', error);
    
    // If we got a CORS error, try with no-cors mode as a fallback
    // This is not ideal but can work in some scenarios
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      try {
        console.log('Attempting to convert document with no-cors mode...');
        
        // Create a new form with the file
        const form = new FormData();
        form.append('files', file);
        
        // Try with no-cors mode
        await fetch('https://convert.ctrlp.co.in/forms/libreoffice/convert', {
          method: 'POST',
          body: form,
          mode: 'no-cors',
        });
        
        // Note: With 'no-cors', we can't read the response properties
        // This is a limitation of the mode, but we'll try to work with what we get
        
        // Since we can't read the response in no-cors mode, we show a more user-friendly message
        throw new Error('CORS issue detected. The document conversion service is not properly configured for browser access.');
      } catch (noCorsError) {
        console.error('Error with no-cors mode:', noCorsError);
        throw new Error('Unable to convert document due to CORS restrictions. Please contact support.');
      }
    }
    
    throw error;
  }
}

/**
 * Process an uploaded file:
 * - For PDFs: Count pages
 * - For HEIC: Convert to JPEG then to PDF
 * - For Office documents: Convert to PDF using Gotenberg
 * - For images: Convert to PDF
 * - For other files: Return as is with page count 1
 */
export async function processFile(file: File): Promise<ProcessedFile> {
  // Check if file is HEIC format and convert if needed
  if (isHeicImage(file)) {
    try {
      // Convert HEIC to JPEG
      const jpegFile = await convertHeicToJpeg(file);
      
      // Now process the JPEG file like any other image
      const pdfFile = await convertImageToPdf(jpegFile);
      return { 
        file: pdfFile, 
        url: URL.createObjectURL(pdfFile), 
        pageCount: 1
      };
    } catch (error) {
      console.error('Error processing HEIC file:', error);
      throw error;
    }
  }
  
  // Check if file is an Office document and convert if needed
  if (isOfficeDocument(file)) {
    try {
      // Convert Office document to PDF
      const pdfFile = await convertOfficeToPdf(file);
      
      // Count pages in the converted PDF
      const pageCount = await getPdfPageCount(pdfFile);
      
      return {
        file: pdfFile,
        url: URL.createObjectURL(pdfFile),
        pageCount
      };
    } catch (error) {
      console.error('Error processing Office document:', error);
      throw error;
    }
  }
    // Create URL for preview
  const url = URL.createObjectURL(file);
  
  console.log(`Processing file in processFile: ${file.name}, type: ${file.type}`);
  
  // Check file type by MIME type first, then by extension if needed
  if (file.type === 'application/pdf') {
    // Process PDF file
    const pageCount = await getPdfPageCount(file);
    return { file, url, pageCount };
  } else if (file.type.startsWith('image/')) {
    console.log(`Converting image to PDF: ${file.name}`);
    // Convert image to PDF
    const pdfFile = await convertImageToPdf(file);
    console.log(`Image converted successfully to PDF: ${pdfFile.name}`);
    return { 
      file: pdfFile, 
      url: URL.createObjectURL(pdfFile), 
      pageCount: 1
    };
  } else {
    // Fallback to checking file extension if MIME type is missing or incorrect
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension === 'jpg' || extension === 'jpeg' || extension === 'png' || extension === 'gif') {
      console.log(`Processing file as image based on extension: ${extension}`);
      try {
        const pdfFile = await convertImageToPdf(file);
        return {
          file: pdfFile,
          url: URL.createObjectURL(pdfFile),
          pageCount: 1
        };
      } catch (error) {
        console.error(`Error converting image by extension: ${error}`);
        // If conversion fails, return original file
        return { file, url, pageCount: 1 };
      }
    }
  }
  
  // Default for other file types
  return { file, url, pageCount: 1 };
}

/**
 * Count pages in a PDF file
 */
export async function getPdfPageCount(file: File): Promise<number> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    return pdfDoc.getPageCount();
  } catch (error) {
    console.error('Error counting PDF pages:', error);
    return 1; // Default if we can't determine the page count
  }
}

/**
 * Convert an image file to a PDF
 */
async function convertImageToPdf(imageFile: File): Promise<File> {
  console.log(`Starting image to PDF conversion for: ${imageFile.name}, type: ${imageFile.type}, size: ${imageFile.size}`);
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    // Handle FileReader errors
    reader.onerror = (error) => {
      console.error(`FileReader error during image conversion:`, error);
      reject(new Error(`FileReader error: ${error}`));
    };
    
    reader.onload = (event) => {
      try {
        console.log(`FileReader loaded image data, creating image element`);
        const img = new Image();
        
        // Handle image loading errors
        img.onerror = (error) => {
          console.error(`Error loading image:`, error);
          reject(new Error(`Image loading failed: ${imageFile.name}`));
        };
        
        img.src = event.target?.result as string;
        
        img.onload = () => {
          try {
            console.log(`Image loaded successfully: ${img.width}x${img.height}`);
            // Determine if image is landscape or portrait
            const isLandscape = img.width > img.height;
            
            // Choose orientation based on image dimensions
            const orientation = isLandscape ? 'l' : 'p';
            
            console.log(`Creating PDF with ${orientation} orientation`);
            const doc = new jsPDF({
              orientation,
              unit: 'px',
            });
            
            // Get PDF page dimensions
            const pdfWidth = doc.internal.pageSize.getWidth();
            const pdfHeight = doc.internal.pageSize.getHeight();
            
            // Calculate dimensions to fit the image properly
            const ratio = Math.min(pdfWidth / img.width, pdfHeight / img.height);
            const width = img.width * ratio;
            const height = img.height * ratio;
            
            // Center the image
            const x = (pdfWidth - width) / 2;
            const y = (pdfHeight - height) / 2;
            
            console.log(`Adding image to PDF: ${x},${y} ${width}x${height}`);
            
            // Add image with proper format based on image type
            const format = imageFile.type.includes('png') ? 'PNG' : 'JPEG';
            doc.addImage(img, format, x, y, width, height);
            
            // Convert to blob and then to File
            const pdfBlob = doc.output('blob');
            const pdfFileName = imageFile.name.replace(/\.[^/.]+$/, '.pdf');
            const pdfFile = new File([pdfBlob], pdfFileName, { type: 'application/pdf' });
            
            console.log(`PDF conversion completed successfully: ${pdfFileName}`);
            resolve(pdfFile);
          } catch (error) {
            console.error(`Error generating PDF:`, error);
            reject(error);
          }
        };
      } catch (error) {
        console.error(`Error in image processing:`, error);
        reject(error);
      }
    };
    
    console.log(`Reading image file as data URL`);
    reader.readAsDataURL(imageFile);
  });
}

/**
 * Combine multiple image files into a single PDF
 */
export async function combineImagesToSinglePdf(imageFiles: File[], outputFilename: string): Promise<File> {
  // Sort files by name to maintain order
  const sortedFiles = [...imageFiles].sort((a, b) => a.name.localeCompare(b.name));
  
  // Create a new PDF document
  const doc = new jsPDF({
    orientation: 'p', // Default to portrait, we'll adjust per image
    unit: 'px',
  });
  
  let isFirstPage = true;
  
  for (const imageFile of sortedFiles) {
    // Convert image to data URL
    const imageDataUrl = await fileToDataUrl(imageFile);
    
    // Create an image element to get dimensions
    const img = await loadImage(imageDataUrl);
    
    // Determine if image is landscape or portrait
    const isLandscape = img.width > img.height;
    
    // If not the first page, add a new page with the right orientation
    if (!isFirstPage) {
      doc.addPage(isLandscape ? 'l' : 'p');
    } else {
      // For the first page, set the orientation if landscape
      if (isLandscape) {
        doc.deletePage(1);
        doc.addPage('l');
      }
      isFirstPage = false;
    }
    
    // Get PDF page dimensions
    const pdfWidth = doc.internal.pageSize.getWidth();
    const pdfHeight = doc.internal.pageSize.getHeight();
    
    // Calculate dimensions to fit the image properly
    const ratio = Math.min(pdfWidth / img.width, pdfHeight / img.height);
    const width = img.width * ratio;
    const height = img.height * ratio;
    
    // Center the image
    const x = (pdfWidth - width) / 2;
    const y = (pdfHeight - height) / 2;
    
    doc.addImage(imageDataUrl, 'JPEG', x, y, width, height);
  }
  
  // Convert to blob and then to File
  const pdfBlob = doc.output('blob');
  const pdfFile = new File([pdfBlob], outputFilename, { type: 'application/pdf' });
  
  return pdfFile;
}

// Helper to convert File to data URL
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Helper to load an image and get its dimensions
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Creates a single-page PDF with front and back ID card images
 * Front image on top, back image on bottom
 */
export const createIdCardPdf = async (frontImage: File, backImage: File, filename: string): Promise<File> => {
  // Create a new PDF document
  const pdfDoc = await PDFDocument.create();
  
  // Add a blank page
  const page = pdfDoc.addPage(PageSizes.A4);
  const { width, height } = page.getSize();
  
  // Convert images to Uint8Array
  const frontImageBytes = await readFileAsArrayBuffer(frontImage);
  const backImageBytes = await readFileAsArrayBuffer(backImage);
  
  // Convert to PDF-friendly formats
  let frontPdfImage, backPdfImage;
  
  // Handle JPG/PNG appropriately
  if (frontImage.type === 'image/jpeg') {
    frontPdfImage = await pdfDoc.embedJpg(frontImageBytes);
  } else if (frontImage.type === 'image/png') {
    frontPdfImage = await pdfDoc.embedPng(frontImageBytes);
  } else {
    throw new Error(`Unsupported image format: ${frontImage.type}`);
  }
  
  if (backImage.type === 'image/jpeg') {
    backPdfImage = await pdfDoc.embedJpg(backImageBytes);
  } else if (backImage.type === 'image/png') {
    backPdfImage = await pdfDoc.embedPng(backImageBytes);
  } else {
    throw new Error(`Unsupported image format: ${backImage.type}`);
  }
  
  // Calculate dimensions for images
  // Standard ID card (credit card) ratio is 85.6mm × 54mm (aspect ratio ~1.586)
  const cardWidth = width * 0.8; // 80% of page width
  const cardHeight = cardWidth / 1.586; // Maintain ID card aspect ratio
  
  const marginX = (width - cardWidth) / 2;
  const marginTop = height * 0.1;
  const marginBottom = height * 0.1;
  
  // Position for front image (top)
  page.drawImage(frontPdfImage, {
    x: marginX,
    y: height - marginTop - cardHeight,
    width: cardWidth,
    height: cardHeight,
  });
  
  // Position for back image (bottom)
  page.drawImage(backPdfImage, {
    x: marginX,
    y: marginBottom,
    width: cardWidth,
    height: cardHeight,
  });
  
  // Add labels for front and back
  // (Note: pdf-lib doesn't directly support text, but in a real implementation
  // you would use drawText here if using a font)
  
  // Save the PDF
  const pdfBytes = await pdfDoc.save();
  
  // Convert to File object
  return new File(
    [pdfBytes], 
    filename, 
    { type: 'application/pdf' }
  );
};

/**
 * Helper function to read a file as ArrayBuffer
 */
const readFileAsArrayBuffer = (file: File): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(reader.result));
      } else {
        reject(new Error('Failed to read file as ArrayBuffer'));
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};
